/**
 * DocumentZoom - the reader's text size, as a control on the page itself.
 *
 * It sits on the DOCUMENT, bottom-right, rather than in the header. The header
 * already carries the version toggles, download and expand, and adding a
 * three-part stepper to that cluster is what pushed the bar into collapsing at
 * 1280 in the first place. This is also where a reader's hand already is: the
 * thing being resized is right there, and the control is out of the text's way
 * in the corner.
 *
 * Quiet until wanted - half-faded at rest, solid on hover or while focused —
 * because it sits over content that is being read.
 */

import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import {
  PiArrowCounterClockwiseLight,
  PiMinusLight,
  PiPlusLight,
} from 'react-icons/pi';

interface Props {
  label: string;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
  canZoomIn: boolean;
  canZoomOut: boolean;
  isDefault: boolean;
}

const STEP_BUTTON =
  'flex h-7 w-7 items-center justify-center rounded-md text-fg-body ' +
  'transition-colors duration-200 ease-slide ' +
  'hover:bg-surface-sunken hover:text-champagne ' +
  'disabled:cursor-default disabled:opacity-35 disabled:hover:bg-transparent ' +
  'disabled:hover:text-fg-body ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/40';

const DocumentZoom: React.FC<Props> = ({
  label, onZoomIn, onZoomOut, onReset, canZoomIn, canZoomOut, isDefault,
}) => {
  const { t } = useTranslation();

  return (
    <div
      data-testid="document-zoom"
      className="pointer-events-auto absolute bottom-4 end-4 z-20 flex items-center gap-0.5
                 rounded-xl border border-hairline bg-surface p-1 shadow-md
                 opacity-60 transition-opacity duration-200 ease-slide
                 hover:opacity-100 focus-within:opacity-100"
    >
      <button
        type="button"
        onClick={onZoomOut}
        disabled={!canZoomOut}
        data-testid="document-zoom-out"
        title={t('Smaller text')}
        aria-label={t('Smaller text')}
        className={STEP_BUTTON}
      >
        <PiMinusLight className="h-4 w-4" />
      </button>

      {/*
        The readout doubles as the reset. A separate reset button would be a
        third control for a state most readers never leave, and "press the
        number to go back to normal" is the pattern a browser's own zoom uses.
      */}
      <button
        type="button"
        onClick={onReset}
        disabled={isDefault}
        data-testid="document-zoom-reset"
        title={isDefault ? t('Text size') : t('Reset text size')}
        aria-label={isDefault ? t('Text size') : t('Reset text size')}
        className={clsx(
          'flex min-w-[3.25rem] items-center justify-center gap-1 rounded-md px-1.5 py-1',
          'text-[11px] font-medium tabular-nums transition-colors duration-200 ease-slide',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/40',
          isDefault
            ? 'cursor-default text-fg-muted'
            : 'text-fg-strong hover:bg-surface-sunken hover:text-champagne',
        )}
      >
        {label}
        {!isDefault && <PiArrowCounterClockwiseLight className="h-3 w-3" aria-hidden />}
      </button>

      <button
        type="button"
        onClick={onZoomIn}
        disabled={!canZoomIn}
        data-testid="document-zoom-in"
        title={t('Larger text')}
        aria-label={t('Larger text')}
        className={STEP_BUTTON}
      >
        <PiPlusLight className="h-4 w-4" />
      </button>
    </div>
  );
};

export default DocumentZoom;
